import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Event } from '@casetrace/shared';
import { Skeleton } from '../../../components/ui/skeleton';
import type { TimelineRow } from '../use-case-timeline';
import type { ToggleHighlightHandler } from '../../../hooks/use-toggle-highlight';
import { TimelineEmptyState } from './timeline-empty-state';
import { TimelineVirtualList } from './timeline-virtual-list';

function TimelineSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      {[3, 2, 2].map((count, gi) => (
        <div key={gi} className="flex flex-col gap-2">
          <Skeleton className="h-3 w-24" />
          {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ))}
    </div>
  );
}

const ESTIMATED_SIZE: Record<TimelineRow['kind'], number> = {
  header: 74,
  event: 86,
  'load-more': 52,
};

interface TimelineProps {
  rows: TimelineRow[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  selectedId: string | null;
  expandedEvent: Event | null;
  highlightedEventIds: ReadonlySet<string>;
  canToggleHighlight: boolean;
  scrollToEventId?: string | null;
  onScrolledToEvent?: (eventId: string) => void;
  onSelect: (event: Event) => void;
  onDeselect: () => void;
  onToggleHighlight: ToggleHighlightHandler;
  fetchNextPage: () => void;
  onOpenSources: () => void;
}

function useScrollToEvent(
  rows: TimelineRow[],
  scrollToEventId: string | null | undefined,
  scrollToIndex: (index: number, opts?: { align?: 'start' | 'center' | 'end' | 'auto' }) => void,
  onScrolledToEvent?: (eventId: string) => void,
) {
  const doneForId = useRef<string | null>(null);

  useEffect(() => {
    if (!scrollToEventId) {
      doneForId.current = null;
      return;
    }
    if (doneForId.current === scrollToEventId) return;
    const index = rows.findIndex((row) => row.kind === 'event' && row.event.id === scrollToEventId);
    if (index < 0) return;
    doneForId.current = scrollToEventId;
    // Dobbel rAF: først til kortet, deretter etter expand/måling.
    requestAnimationFrame(() => {
      scrollToIndex(index, { align: 'center' });
      requestAnimationFrame(() => {
        scrollToIndex(index, { align: 'center' });
        onScrolledToEvent?.(scrollToEventId);
      });
    });
  }, [rows, scrollToEventId, scrollToIndex, onScrolledToEvent]);
}

export function Timeline({
  rows,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  selectedId,
  expandedEvent,
  highlightedEventIds,
  canToggleHighlight,
  scrollToEventId = null,
  onScrolledToEvent,
  onSelect,
  onDeselect,
  onToggleHighlight,
  fetchNextPage,
  onOpenSources,
}: TimelineProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => ESTIMATED_SIZE[rows[i]?.kind ?? 'event'],
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const last = virtualItems[virtualItems.length - 1];
    if (last && last.index >= rows.length - 3) fetchNextPage();
  }, [virtualItems, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useScrollToEvent(rows, scrollToEventId, virtualizer.scrollToIndex, onScrolledToEvent);

  if (isLoading) {
    return (
      <div ref={parentRef} className="h-full overflow-y-auto">
        <TimelineSkeleton />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div ref={parentRef} className="h-full overflow-y-auto">
        <TimelineEmptyState onOpenSources={onOpenSources} />
      </div>
    );
  }

  return (
    <div ref={parentRef} className="h-full overflow-y-auto">
      <TimelineVirtualList
        virtualizer={virtualizer}
        virtualItems={virtualItems}
        rows={rows}
        selectedId={selectedId}
        expandedEvent={expandedEvent}
        highlightedEventIds={highlightedEventIds}
        canToggleHighlight={canToggleHighlight}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onToggleHighlight={onToggleHighlight}
      />
    </div>
  );
}
